import React, {
  createContext,
  ReactNode,
  useContext,
  useRef,
  useState,
} from "react";
import NoticesListProvider from "./NoticesListProvider";
import { FollowingNoticesList } from "../../../model/notices/NoticesListModel";
import SessionStorage from "../../../service/storage/SessionStorage";
import StorageKeys from "../../../const/StorageKeys";
import { appRoutes } from "../../../routes/routes";
import { AlertConfirm } from "../../../widget/common/ui/Alerts";
import UtilityHelper from "../../../service/utilities/UtilityHelper";
import Loader from "../../../widget/common/ui/Loader";
import { isMobile } from "react-device-detect";

type NoticesListContextType = {
  noticesList: Array<FollowingNoticesList> | null;
  getNoticesListAPI: () => void;
  onClickNotice: (branch: FollowingNoticesList) => void;
  handleClickDeleteConfirm: () => void;
  handlePressStart: (branch: FollowingNoticesList) => void;
  handlePressEnd: () => void;

  longPressStatus: boolean;
  selectedNoticeIds: Array<string>;

  showSearch: boolean;
  searchValue: string;
  handleOnChange: (name: string, value: string) => void;
  clearSearch: () => void;
  updateShowSearch: () => void;
};

const NoticesListContext = createContext<NoticesListContextType | undefined>(
  undefined
);

export const NoticesListContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const isLongPressRef = useRef<boolean>(false);
  const longPressStatusRef = useRef<boolean>(false);

  const searchValueRef = useRef<string>("");
  const noticesListResultRef = useRef<Array<FollowingNoticesList> | []>([]);

  const [showSearch, setShowSearch] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>("");

  const [longPressStatus, setLongPressStatus] = useState<boolean>(false);
  const [selectedNoticeIds, setSelectedNoticeIds] = useState<Array<string>>([]);
  const [noticesList, setNoticesList] =
    useState<Array<FollowingNoticesList> | null>(null);

  const resetValues = () => {
    // DELETE SESSION DETAIL
    SessionStorage.deleteValue(StorageKeys.notices_detail);

    setSelectedNoticeIds([]);
    setLongPressStatus(false);
    isLongPressRef.current = false;
    longPressStatusRef.current = false;
    timerRef.current = null;
  };

  const getNoticesSession = (): boolean => {
    const noticesListSession: Array<FollowingNoticesList> | null =
      UtilityHelper.isNotEmpty(
        SessionStorage.getValue(StorageKeys.home_notices) || ""
      )
        ? JSON.parse(SessionStorage.getValue(StorageKeys.home_notices))
        : null;

    setNoticesList(noticesListSession);
    noticesListResultRef.current = noticesListSession!;

    return noticesListSession != null;
  };

  const getNoticesListAPI = async () => {
    resetValues();
    getNoticesSession();

    const noticesListModel = await NoticesListProvider.getNoticesListAPI();
    if (noticesListModel !== null) {
      const noticesListResult: Array<FollowingNoticesList> | null =
        noticesListModel?.result?.following_notices_list!;

      setNoticesList(noticesListResult);
      noticesListResultRef.current = noticesListResult;

      SessionStorage.saveValue(
        StorageKeys.home_notices,
        JSON.stringify(noticesListResult)
      );
    }
  };

  const deleteNoticeAPI = async () => {
    const noticeSession: string = SessionStorage.getValue(
      StorageKeys.notices_detail
    );
    let action: number = 1;
    let noticeIds: Array<string> = [];

    if (UtilityHelper.isNotEmpty(noticeSession)) {
      const noticeDetailSession: FollowingNoticesList =
        JSON.parse(noticeSession);
      noticeIds.push(noticeDetailSession?.group_id!);
      action = 1;
    }

    if (longPressStatusRef.current && selectedNoticeIds.length > 0) {
      noticeIds = [...selectedNoticeIds];
      action = 2;
    }

    if (noticeIds.length > 0) {
      Loader.modal();
      const status: boolean = await NoticesListProvider.deleteNoticeAPI(
        noticeIds
      );
      Loader.modal(false);

      if (status) {
        if (action === 1) {
          NoticesListProvider.navigate(appRoutes.routeNameNotices, {
            replace: true,
          });
        } else {
          getNoticesListAPI();
        }
      }
    }
  };

  const handleClickDeleteConfirm = () => {
    AlertConfirm({
      title: "are_you_sure_you_wish_to_delete_this_notice",
      onConfirm: async () => {
        deleteNoticeAPI();
      },
    });
  };

  const onClickNotice = (notice: FollowingNoticesList) => {
    if (!longPressStatusRef.current) {
      SessionStorage.saveValue(
        StorageKeys.notices_detail,
        JSON.stringify(notice)
      );

      NoticesListProvider.navigate(appRoutes.routeNameNoticesDetail);
    } else {
      let ids = [...selectedNoticeIds];
      const id = notice?.group_id!;

      if (ids.includes(id)) {
        ids = ids.filter((item) => item !== id);
      } else {
        ids.push(id);
      }
      setSelectedNoticeIds(ids);

      if (ids.length === 0) {
        longPressStatusRef.current = false;
        setLongPressStatus(false);
      }
    }
  };

  const handlePressStart = (notice: FollowingNoticesList) => {
    isLongPressRef.current = false;

    timerRef.current = setTimeout(() => {
      isLongPressRef.current = true;
      longPressStatusRef.current = true;
      setLongPressStatus(true);
      console.log("Long press detected!");

      if (isMobile) {
        let ids = [...selectedNoticeIds];
        ids.push(notice?.group_id!);
        setSelectedNoticeIds(ids);
      }
    }, 500);
  };

  const handlePressEnd = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    console.log("Press and hold");
  };

  const handleOnChange = (fieldName: string, fieldValue: string) => {
    fieldName = fieldName.toLowerCase();
    if (fieldName === `search-notices`) {
      setSearchValue(fieldValue);
      searchValueRef.current = fieldValue;

      const filtered: Array<FollowingNoticesList> | undefined =
        noticesListResultRef.current?.filter((team) =>
          Object.values(team)
            .join(" ")
            .toLowerCase()
            .includes(searchValueRef.current.toLowerCase())
        );

      setNoticesList(filtered!);
    }
  };

  const clearSearch = () => {
    if (UtilityHelper.isEmpty(searchValueRef.current)) {
      updateShowSearch();
    }
    setNoticesList(noticesListResultRef.current);
    setSearchValue("");
    searchValueRef.current = "";
  };

  const updateShowSearch = () => {
    setShowSearch(!showSearch);
  };

  return (
    <NoticesListContext.Provider
      value={{
        noticesList,
        longPressStatus,
        selectedNoticeIds,
        getNoticesListAPI,
        onClickNotice,
        handleClickDeleteConfirm,
        handlePressStart,
        handlePressEnd,

        showSearch,
        searchValue,
        handleOnChange,
        clearSearch,
        updateShowSearch,
      }}
    >
      {children}
    </NoticesListContext.Provider>
  );
};

// IMPORT / USE CONTEXT ///////////////////////////////////////////////////////////////////
export const NoticesListContextImport = () => {
  const context = useContext(NoticesListContext);
  if (!context) {
    throw new Error(
      "NoticesListContextImport must be used within a NoticesListContextProvider"
    );
  }
  return context;
};
